import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { REGIONS, INSETS, anchorFor, IS_PROVINCE, W, H } from './geo/usGeo.js'
import {
  FULL_VIEW, MAX_K, clampView, zoomOf, zoomBy, zoomTo, panByFraction,
  fitToBox, viewBoxOf, isFullyOut, touchActionFor,
} from './geo/mapView.js'
import { NATIONAL, stateName } from '../../lib/usStates.js'
import { pursuitMeta, PURSUIT_META, repColor } from '../../lib/championships.js'
import styles from './CrownMap.module.css'

/**
 * Real North-America choropleth. US states + Canadian PROVINCES render
 * individually (each fillable/clickable, keyed by code); Alaska & Hawaii are
 * insets. State/province fill = dominant pursuit status (or brass footprint
 * highlight in conference mode). Crown markers sit on crowned regions.
 *
 * CATEGORY-AWARE NOUN. The map is a SHARED view — every category space renders
 * it — so it may not hardcode the championship wording. `noun` / `nounPlural`
 * come from the active category (`mapNounFor` in categories.js) and name every
 * marker, tooltip, count and header. The defaults here are deliberately the
 * GENERIC noun, not "championship": a caller that forgets to pass one gets
 * "opportunities", which is true of every space, instead of calling a police
 * department or an RFP a championship, which is true of none of them.
 * Championships passes "championship / championships", so its copy is verbatim
 * what it always was.
 */
export default function CrownMap({
  byState, mode, footprint, selected, onSelect,
  noun = 'opportunity', nounPlural = 'opportunities',
}) {
  const [hover, setHover] = useState(null)

  /* ---- ZOOM AND PAN ---------------------------------------------------- *
   * Brian asked for this three times, and mobile is the primary case: at the
   * full extent Rhode Island is about four pixels across on a 390px phone, so
   * the small states were not merely fiddly, they were unhittable.
   *
   * All the arithmetic lives in geo/mapView.js so it can be driven under Node
   * (verify-map-zoom.mjs). This component owns only the DOM: where the fingers
   * are, and what fraction of the element that is. */
  const [view, setView] = useState(FULL_VIEW)
  const svgRef = useRef(null)
  const boxRef = useRef(null)
  /* Live pointer positions, by pointerId — a Map rather than two slots because
   * a third finger landing mid-pinch must not be read as the second one moving. */
  const ptrs = useRef(new Map())
  const gesture = useRef(null)
  /* ⚠️ A DRAG THAT ENDS ON A STATE MUST NOT SELECT IT. Pointer-up fires a click,
   * so panning across the map would open the panel for whichever state happened
   * to be under the finger at the end — losing the reader's place on a surface
   * whose whole point is keeping it. Set once a drag passes the slop threshold
   * and consumed by the next click. */
  const dragged = useRef(false)

  const k = zoomOf(view)
  const zoomedIn = !isFullyOut(view)

  /** Where a client point falls inside the map box, as a 0..1 fraction. */
  const fracOf = useCallback((clientX, clientY) => {
    const r = boxRef.current?.getBoundingClientRect()
    if (!r || !r.width || !r.height) return [0.5, 0.5]
    return [(clientX - r.left) / r.width, (clientY - r.top) / r.height]
  }, [])

  const onPointerDown = useCallback((e) => {
    /* Only trace the gestures we handle. A pen or a right-click is left alone. */
    if (e.button != null && e.button !== 0) return
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (ptrs.current.size === 1) {
      dragged.current = false
      /* ⚠️ ONE FINGER ONLY PANS WHEN THERE IS SOMEWHERE TO PAN TO. Fully zoomed
       * out the page keeps the gesture (see touchActionFor), so arming a pan
       * here would fight the scroll the reader actually asked for. */
      gesture.current = zoomedIn ? { kind: 'pan', x: e.clientX, y: e.clientY } : null
    } else if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y)
      gesture.current = { kind: 'pinch', dist: dist || 1, k }
    }
  }, [zoomedIn, k])

  const onPointerMove = useCallback((e) => {
    if (!ptrs.current.has(e.pointerId)) return
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const g = gesture.current
    if (!g) return

    if (g.kind === 'pinch' && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()]
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1
      const [fx, fy] = fracOf((a.x + b.x) / 2, (a.y + b.y) / 2)
      dragged.current = true
      setView((v) => zoomTo(v, g.k * (dist / g.dist), fx, fy))
      return
    }

    if (g.kind === 'pan') {
      const r = boxRef.current?.getBoundingClientRect()
      if (!r?.width) return
      const dx = e.clientX - g.x
      const dy = e.clientY - g.y
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragged.current = true
      gesture.current = { ...g, x: e.clientX, y: e.clientY }
      /* Drag the MAP with the finger, so the content follows the hand — hence
       * the negative sign. Fractions of the element, so the same travel moves
       * the same distance under the finger at every zoom level. */
      setView((v) => panByFraction(v, -dx / r.width, -dy / r.height))
    }
  }, [fracOf])

  const endPointer = useCallback((e) => {
    ptrs.current.delete(e.pointerId)
    if (ptrs.current.size === 1 && gesture.current?.kind === 'pinch') {
      /* A pinch released one finger at a time. Re-arm as a pan from where the
       * remaining finger is rather than leaving a stale pinch that would jump
       * the zoom on the next move. */
      const [only] = [...ptrs.current.values()]
      gesture.current = zoomedIn ? { kind: 'pan', x: only.x, y: only.y } : null
    } else if (!ptrs.current.size) {
      gesture.current = null
    }
  }, [zoomedIn])

  /* ⚠️ WHEEL ZOOM TAKES A MODIFIER, DELIBERATELY. A bare wheel over a map that
   * fills a phone-height block on a long scrolling page swallows the page
   * scroll, which is the same trap `touch-action: none` sets with a finger.
   * Ctrl/⌘+wheel is also what a trackpad pinch already sends, so a pinch on a
   * laptop works with no extra code. */
  useEffect(() => {
    const el = boxRef.current
    if (!el) return undefined
    const onWheel = (e) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const [fx, fy] = fracOf(e.clientX, e.clientY)
      setView((v) => zoomBy(v, e.deltaY < 0 ? 1.18 : 1 / 1.18, fx, fy))
    }
    /* Passive must be false or preventDefault is ignored, and React's onWheel
     * is passive by default — which is why this is a manual listener. */
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [fracOf])

  const onDoubleClick = useCallback((e) => {
    const [fx, fy] = fracOf(e.clientX, e.clientY)
    setView((v) => zoomBy(v, 2, fx, fy))
  }, [fracOf])

  /* "Zoom to <state>" reads the REAL rendered geometry rather than a table of
   * bounding boxes kept beside the paths. A second copy of the geometry is a
   * second thing to drift, and `getBBox()` is already exact. */
  const zoomToSelected = useCallback(() => {
    const node = svgRef.current?.querySelector(`[data-code="${selected}"]`)
    if (!node?.getBBox) return
    try { setView(fitToBox(node.getBBox())) } catch { /* detached node — leave the view alone */ }
  }, [selected])

  const resetView = useCallback(() => setView(FULL_VIEW), [])

  /* Keyboard pan/zoom, so the map is not mouse-and-finger only. */
  const onKeyDown = useCallback((e) => {
    const step = 0.18
    const moves = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step],
    }
    if (moves[e.key]) {
      if (!zoomedIn) return
      e.preventDefault()
      setView((v) => panByFraction(v, ...moves[e.key]))
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault(); setView((v) => zoomBy(v, 1.4))
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault(); setView((v) => zoomBy(v, 1 / 1.4))
    } else if (e.key === '0') {
      e.preventDefault(); resetView()
    }
  }, [zoomedIn, resetView])
  /* ---- end zoom and pan ------------------------------------------------ */

  /* WHAT THE FILL MEANS, chosen here rather than by the caller. Every space that
   * renders this map gets the rep view for free, and no page has to be edited to
   * grant it. Defaults to `stage` so the surface nobody touches is unchanged. */
  const [fillBy, setFillBy] = useState('stage')
  // "Championship Map" / "RFP Map" / "Account Map" — the space's own word,
  // title-cased without disturbing an acronym ("RFP" stays "RFP").
  const title = `${noun.charAt(0).toUpperCase()}${noun.slice(1)} Map`

  /* ⚠️ ONE CROWN NO LONGER PAINTS A WHOLE STATE.
   *
   * This used to return the Champion-Crowned colour whenever `s.crowned` was
   * true, BEFORE looking at the dominant stage — so a state holding 500
   * untouched records behind a single crowned championship rendered as done, on
   * the most-looked-at surface in the product. `crowned` is a boolean ("at least
   * one"), and a boolean must never outrank a distribution.
   *
   * The crown is already communicated, and correctly: the ♛ markers below sit on
   * exactly the regions that have one, and the tooltip now states how many. So
   * the FILL answers "what is the state of the work here" and the MARKER answers
   * "has anything been won here" — two questions, two channels, neither
   * impersonating the other.
   *
   * ⚠️ AND THAT 2.78.0 FIX WAS INCOMPLETE UNTIL 2.87.0 — READ THIS BEFORE
   * TRUSTING THE PARAGRAPH ABOVE. Removing `s.crowned` here did not end the
   * defect, because `s.dominant` was itself a MAX over a priority table in which
   * 'Champion Crowned' ranks highest. A state with 500 untouched records and one
   * crown still filled Champion-Crowned green, and the tooltip still said "most
   * common" about it. The sentence "a boolean must never outrank a distribution"
   * was right; what it fell through to simply was not a distribution.
   * `aggregateByState` now computes the true mode and carries the margin — see
   * the note there, and verify-map-fill.mjs, which fails on the 500-and-one
   * shape. The lesson worth keeping: a fix that removes the symptom it can see
   * is not the same as a fix to the mechanism. */
  const fillFor = (code) => {
    if (mode === 'conference') return footprint && footprint.has(code) ? 'var(--brass)' : 'var(--map-base)'
    const s = byState[code]
    if (!s || !s.count) return 'var(--map-base)'
    /* Brian: "make it so the states show color of rep." A region with records
     * and nobody on them stays base-coloured rather than borrowing a rep's
     * colour — an unassigned state must not look like somebody's. */
    if (fillBy === 'rep') return s.dominantRep ? repColor(s.dominantRep) : 'var(--map-base)'
    return pursuitMeta(s.dominant).color
  }

  const crownRegions = useMemo(
    () => Object.values(byState).filter((s) => s.crowned).map((s) => ({ code: s.code, xy: anchorFor(s.code) })).filter((s) => s.xy),
    [byState]
  )

  const statesWithData = Object.keys(byState).filter((c) => c !== NATIONAL && byState[c].count).length
  const hoverInfo = hover ? byState[hover.code] : null

  /* Reps actually holding the most records in at least one region on screen,
   * with how many regions they lead, so the legend is ordered by presence. */
  const repsOnMap = useMemo(() => {
    const m = new Map()
    for (const s of Object.values(byState)) {
      if (!s.count || !s.dominantRep) continue
      m.set(s.dominantRep, (m.get(s.dominantRep) || 0) + 1)
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
      .map(([rep, states]) => ({ rep, states }))
  }, [byState])

  /* ⚠️ MONEY ON THIS MAP IS A SUM OF RECORDED AMOUNTS, AND SAYS SO.
   * `s.tam` was already being computed on every page load and read nowhere —
   * one occurrence in the whole bundle. It is surfaced here with its
   * DENOMINATOR, because the sum alone is the misleading half: $4M across 3 of
   * 500 records is a fact about 3 records, and a reader who is not told that
   * will take it for the state's worth. It is NOT the modelled chain (field
   * size × units per buyer × penetration × price) and is never called TAM. */
  const money = (n) => '$' + Math.round(n).toLocaleString('en-US')

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>{title}</div>
          <div className={styles.hint}>
            {mode === 'conference'
              ? 'Conference footprint highlighted — click a state or province to drill in'
              : `${statesWithData} region${statesWithData === 1 ? '' : 's'} with ${nounPlural} · US states + Canadian provinces`}
          </div>
        </div>
        {mode === 'state' && (
          <div className={styles.modeSwitch} role="group" aria-label="What the map colour means">
            {[['stage', 'By stage'], ['rep', 'By rep']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={styles.modeBtn}
                data-on={fillBy === k}
                aria-pressed={fillBy === k}
                onClick={() => setFillBy(k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {mode === 'state' && fillBy === 'stage' && (
          <div className={styles.legend}>
            {['To Pursue', 'Outreach In Progress', 'Won', 'Champion Crowned'].map((k) => (
              <span key={k} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: PURSUIT_META[k].color }} />
                {PURSUIT_META[k].label}
              </span>
            ))}
          </div>
        )}
        {/* THE REP LEGEND IS BUILT FROM WHAT IS ON THE MAP, not from a roster.
            A legend listing every rep in the company would show colours for
            people holding nothing in view, and a rep looking for their own
            colour would find it next to an empty map. */}
        {mode === 'state' && fillBy === 'rep' && (
          <div className={styles.legend}>
            {repsOnMap.map(({ rep, states }) => (
              <span key={rep} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: repColor(rep) }} />
                {rep}
                <span className={styles.legendCount}>{states}</span>
              </span>
            ))}
            {!repsOnMap.length && <span className={styles.legendItem}>Nobody is assigned anything in view.</span>}
          </div>
        )}
      </div>

      {/* 🔴 THE ZOOM CONTROLS ARE REAL BUTTONS, NOT A GESTURE SECRET.
        *
        * A pinch is undiscoverable and a wheel needs a modifier, so a map whose
        * only way in is a gesture is a map most people never zoom. These are
        * also the whole of the keyboard and assistive story, and they are the
        * reason the feature is usable on a desktop with no trackpad.
        *
        * ⚠️ "Zoom to" is DISABLED WITH ITS REASON when nothing is selected
        * rather than hidden — a control that appears and disappears as you
        * click around the map is harder to learn than one that is always
        * there and says what it needs. */}
      <div className={styles.mapCtrls} role="group" aria-label="Zoom and pan the map">
        <button type="button" className={styles.mapCtrl} onClick={() => setView((v) => zoomBy(v, 1.4))}
          disabled={zoomOf(view) >= MAX_K}
          title={zoomOf(view) >= MAX_K ? 'Already as close as the map goes' : undefined}>
          Zoom in
        </button>
        <button type="button" className={styles.mapCtrl} onClick={() => setView((v) => zoomBy(v, 1 / 1.4))}
          disabled={!zoomedIn} title={!zoomedIn ? 'Already showing everything' : undefined}>
          Zoom out
        </button>
        <button type="button" className={styles.mapCtrl} onClick={zoomToSelected}
          disabled={!selected}
          title={!selected ? 'Pick a state on the map first, then this frames it' : undefined}>
          Zoom to {selected ? stateName(selected) : 'a state'}
        </button>
        <button type="button" className={styles.mapCtrl} onClick={resetView} disabled={!zoomedIn}
          title={!zoomedIn ? 'Already showing everything' : undefined}>
          Reset
        </button>
        <span className={styles.mapZoomRead} aria-live="polite">
          {zoomedIn ? `${zoomOf(view).toFixed(1)}× — drag to move` : 'Whole map'}
        </span>
      </div>

      <div
        className={styles.mapBox}
        ref={boxRef}
        /* ⚠️ `touch-action` IS THE SCROLL-TRAP RULE AND IT IS NOT COSMETIC.
         * Fully zoomed out this is `pan-y`, so one finger still scrolls the
         * PAGE past a map that fills a phone screen; zoomed in it becomes
         * `none` and the drag pans the map. Reset therefore hands page
         * scrolling back, which is why Reset is a real control. */
        style={{ touchAction: touchActionFor(view) }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={endPointer}
        onDoubleClick={onDoubleClick}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="application"
        aria-label={`${title} — arrow keys pan, plus and minus zoom, 0 resets`}
      >
        <svg ref={svgRef} viewBox={viewBoxOf(view)} className={styles.svg} role="group"
          aria-label={`US and Canada ${noun} map`}>
          {/* Inset frames for Alaska / Hawaii */}
          {INSETS.map((ins) => (
            <g key={ins.code} className={styles.insetFrame}>
              <rect x={ins.x} y={ins.y} width={ins.w} height={ins.h} rx="6" />
              <text x={ins.x + 6} y={ins.y + 14}>{ins.label}</text>
            </g>
          ))}

          {/* All regions: US states + provinces + AK/HI insets */}
          {REGIONS.map((r) => {
            const isSel = selected === r.code
            const has = byState[r.code]?.count
            return (
              <path
                key={r.code}
                d={r.d}
                className={`${styles.state} ${has ? styles.hasData : ''} ${IS_PROVINCE(r.code) ? styles.province : ''}`}
                fill={fillFor(r.code)}
                stroke={isSel ? 'var(--ink)' : 'var(--map-line)'}
                strokeWidth={isSel ? 2 : 0.6}
                /* ⚠️ A DRAG THAT ENDS ON A STATE MUST NOT SELECT IT. Pointer-up
                 * fires a click, so panning across the map would open the panel
                 * for whichever state happened to be under the finger when it
                 * lifted — losing the reader's place on a surface whose whole
                 * point is keeping it. The flag is set once a drag passes the
                 * slop threshold and is consumed here. */
                onClick={() => {
                  if (dragged.current) { dragged.current = false; return }
                  onSelect(r.code)
                }}
                onMouseMove={(e) => setHover({ code: r.code, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
                onMouseLeave={() => setHover(null)}
                aria-label={`${r.name}: ${byState[r.code]?.count || 0} ${nounPlural}`}
              />
            )
          })}

          {/* Crown markers */}
          {crownRegions.map((s) => (
            <g key={s.code} transform={`translate(${s.xy[0]}, ${s.xy[1]})`} className={styles.crownG}>
              <circle r="9" className={styles.crownDisc} />
              <text className={styles.crownGlyph} y="3.5" textAnchor="middle">♛</text>
            </g>
          ))}
        </svg>

        {hoverInfo != null && (
          <div className={styles.tip} style={{ left: hover.x + 14, top: hover.y + 14 }}>
            <div className={styles.tipName}>{stateName(hover.code)}{IS_PROVINCE(hover.code) ? ' 🍁' : ''}</div>
            <div className={styles.tipRow}>
              {hoverInfo.count} {hoverInfo.count === 1 ? noun : nounPlural}
            </div>
            {/* ⚠️ THE FILL QUOTES ITS OWN MARGIN, like the rep line below.
                This row used to read "Champion Crowned (most common)" — a claim
                that was simply untrue, because `dominant` was the single most
                ADVANCED record in the state, not the most common one. One crown
                among 500 untouched records produced that exact sentence. The
                count behind the colour is what makes it checkable. */}
            <div className={styles.tipRow}>
              {hoverInfo.dominant
                ? <>
                    {pursuitMeta(hoverInfo.dominant).label}
                    <span className={styles.tipDim}>
                      {' '}{hoverInfo.dominantCount} of {hoverInfo.count}
                      {hoverInfo.stageCount > 1 ? ` · ${hoverInfo.stageCount} stages here` : ''}
                    </span>
                  </>
                : <span className={styles.tipDim}>No pursuit status recorded here</span>}
            </div>
            {/* WHO IS ON IT. Stated with the share, because a rep's name alone
                over a state they hold a minority of — say, 40 of 300 records,
                hypothetically — would read as the state being theirs. */}
            <div className={styles.tipRow}>
              {hoverInfo.dominantRep
                ? <>
                    {hoverInfo.dominantRep}
                    <span className={styles.tipDim}>
                      {' '}{hoverInfo.dominantRepRecords} of {hoverInfo.count}
                      {hoverInfo.repCount > 1 ? ` · ${hoverInfo.repCount} reps here` : ''}
                    </span>
                  </>
                : <span className={styles.tipDim}>No rep assigned</span>}
            </div>
            {/* The money, with the denominator that makes it honest. */}
            <div className={styles.tipRow}>
              {hoverInfo.valueRecords > 0
                ? <>
                    {money(hoverInfo.tam)}
                    <span className={styles.tipDim}>
                      {' '}recorded on {hoverInfo.valueRecords} of {hoverInfo.count}
                    </span>
                  </>
                : <span className={styles.tipDim}>No amount recorded on any record here</span>}
            </div>
            {/* ⚠️ COUNTED, NOT ASSERTED. "champion crowned" as a bare line let one
                crown stand for a whole state. The number is the correction. */}
            {hoverInfo.crownedCount > 0 && (
              <div className={styles.tipCrown}>
                ♛ {hoverInfo.crownedCount} of {hoverInfo.count} crowned
              </div>
            )}
          </div>
        )}
        {hover && !hoverInfo && (
          <div className={styles.tip} style={{ left: hover.x + 14, top: hover.y + 14 }}>
            <div className={styles.tipName}>{stateName(hover.code)}{IS_PROVINCE(hover.code) ? ' 🍁' : ''}</div>
            <div className={styles.tipRow}>No {nounPlural}</div>
          </div>
        )}
      </div>

      <div className={styles.footRow}>
        <button
          type="button"
          className={`${styles.national} ${selected === NATIONAL ? styles.nationalActive : ''}`}
          onClick={() => onSelect(NATIONAL)}
          title="USA / Canada (unspecified) / blank location"
        >
          <span className={styles.nationalDot} />
          National / unspecified
          <span className={styles.nationalCount}>{byState[NATIONAL]?.count || 0}</span>
        </button>
        {selected && (
          <button type="button" className="chip" style={{ cursor: 'pointer' }} onClick={() => onSelect(null)}>
            Clear selection ✕
          </button>
        )}
      </div>
    </div>
  )
}
